import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { formatTimeAgo } from "views_shared/app/javascript/utils/dateFormatters";
import SearchableTable from "../shared/SearchableTable";
import { createPostgRESTDataAdapter } from "../shared/dataAdapters";
import MatchedRules from "./MatchedRules";
import UserVerificationActions from "./UserVerificationActions";
import UserVerificationRulesPanel from "./UserVerificationRulesPanel";
import {
  DECISIONS_URL,
  IP_SOURCE_LABELS,
  OUTCOME_BADGE,
  OUTCOME_LABELS,
  QUEUE_RESOURCE,
  RESOLVED_ELSEWHERE_LABELS,
  SUPABASE_URL,
  USER_TYPE_LABELS,
  VERDICT_BADGE,
  VERDICT_LABELS,
  humanize,
  writeHeaders,
} from "./userVerificationApi";

const TABS = [
  { key: "new", label: "New", icon: "fa-user-plus text-primary", badge: "bg-primary" },
  { key: "audited", label: "Audited", icon: "fa-user-shield text-danger", badge: "bg-danger" },
  {
    key: "suspicious",
    label: "Suspicious",
    icon: "fa-triangle-exclamation text-warning",
    badge: "bg-warning text-dark",
  },
  { key: "reviewed", label: "Reviewed" },
  { key: "all", label: "All" },
  { key: "rules", label: "Rules", icon: "fa-gear", end: true },
];

const VALID_TAB_KEYS = new Set(TABS.map((tab) => tab.key));
const REVIEW_TAB_KEYS = TABS.filter((tab) => tab.badge).map((tab) => tab.key);

const USER_TYPE_FILTERS = [
  { key: "all", label: "All" },
  { key: "media", label: "Media", filter: "eq.media" },
  { key: "promoter", label: "Sources", filter: "eq.promoter" },
];

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

const NO_WRAP = { whiteSpace: "nowrap" };
const FIT_CONTENT = { width: "1%", minWidth: 0, whiteSpace: "nowrap" };

// Every key is a column of admin_user_verification_queue (the adapter selects them all).
// User type shows under the user and country under the IP, so the table fits beside the side nav.
const COLUMNS = {
  id: { type: "number", hidden: true },
  account_user_id: { type: "string", hidden: true },
  v1_user_id: { type: "number", hidden: true },
  user_name: { type: "string", display_as: "User", style: { minWidth: "240px" } },
  email: { type: "string", hidden: true },
  user_type: { type: "string", hidden: true },
  vetted: { type: "boolean", hidden: true },
  under_audit_at: { type: "datetime", hidden: true },
  disabled_at: { type: "datetime", hidden: true },
  profile_slug: { type: "string", hidden: true },
  headline: { type: "string", hidden: true },
  title_at_workplace: { type: "string", hidden: true },
  linkedin_url: { type: "string", hidden: true },
  first_request_id: { type: "string", hidden: true },
  first_request_title: { type: "string", hidden: true },
  first_request_posted_at: { type: "datetime", hidden: true },
  user_created_at: { type: "datetime", display_as: "Signed up", style: FIT_CONTENT },
  signup_code: { type: "string", display_as: "Signup", hidden: true, style: { minWidth: "160px" } },
  signup_code_name: { type: "string", hidden: true },
  referrer: { type: "string", hidden: true },
  referring_domain: { type: "string", hidden: true },
  ...Object.fromEntries(UTM_KEYS.map((key) => [key, { type: "string", hidden: true }])),
  ip: { type: "string", display_as: "Signup IP", style: NO_WRAP },
  ip_source: { type: "string", hidden: true },
  country: { type: "string", hidden: true },
  matched_rules: { type: "string", display_as: "Matched rules", style: { minWidth: "130px" } },
  inputs_missing: { type: "string", hidden: true },
  reasons: { type: "string", hidden: true },
  outcome: { type: "string", display_as: "Outcome", style: NO_WRAP },
  reason: { type: "string", hidden: true },
  audit_awaiting_review: { type: "boolean", hidden: true },
  awaiting_review: { type: "boolean", hidden: true },
  resolved_elsewhere: { type: "string", hidden: true },
  applied_under_audit_at: { type: "datetime", hidden: true },
  review_verdict: { type: "string", display_as: "Verdict", style: { minWidth: "140px" } },
  reviewed_by_name: { type: "string", hidden: true },
  reviewed_at: { type: "datetime", hidden: true },
  review_comment: { type: "string", hidden: true },
  created_at: { type: "datetime", display_as: "Decided", style: FIT_CONTENT },
};

const hide = (column) => ({ ...column, hidden: true });
const show = (column) => ({ ...column, hidden: false });

// Rows on the Audited and Suspicious tabs never carry a verdict.
const REVIEW_COLUMNS = {
  ...COLUMNS,
  signup_code: show(COLUMNS.signup_code),
  review_verdict: hide(COLUMNS.review_verdict),
};

// The New tab's id column holds the bulk-select checkbox; every row there is no_action and unreviewed.
const NEW_COLUMNS = {
  ...REVIEW_COLUMNS,
  id: { ...COLUMNS.id, hidden: false, display_as: "Select", style: FIT_CONTENT },
  matched_rules: hide(COLUMNS.matched_rules),
  outcome: hide(COLUMNS.outcome),
  created_at: hide(COLUMNS.created_at),
};

const COLUMNS_BY_TAB = { new: NEW_COLUMNS, audited: REVIEW_COLUMNS, suspicious: REVIEW_COLUMNS };

const SEARCH_COLUMNS = ["user_name", "email", "ip", "country"];

// awaiting_review ends a review after the server's window, so nobody has to clear it. The
// windowed tabs also send a wider created_at bound, which lets the query use an index.
const TAB_CONFIGS = {
  new: {
    staticFilters: { outcome: "eq.no_action", review_verdict: "is.null", awaiting_review: "eq.true" },
    windowed: true,
    defaultSort: { created_at: "desc" },
  },
  audited: { staticFilters: { audit_awaiting_review: "eq.true" }, defaultSort: { created_at: "asc" } },
  suspicious: {
    staticFilters: { outcome: "eq.suspicious", review_verdict: "is.null", awaiting_review: "eq.true" },
    windowed: true,
    defaultSort: { created_at: "desc" },
  },
  // "and" rather than "or", which the search box sets.
  reviewed: {
    staticFilters: { and: "(or(review_verdict.not.is.null,resolved_elsewhere.not.is.null))" },
    defaultSort: { created_at: "desc" },
  },
  all: { staticFilters: {}, defaultSort: { created_at: "desc" } },
};

const DAY_MS = 24 * 60 * 60 * 1000;

function tabFilters(tabConfig, windowStart) {
  return tabConfig.windowed
    ? { ...tabConfig.staticFilters, created_at: `gte.${windowStart}` }
    : tabConfig.staticFilters;
}

function getTabFromUrl() {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return tab && VALID_TAB_KEYS.has(tab) ? tab : "new";
}

function setTabInUrl(tab) {
  const url = new URL(window.location);
  url.searchParams.set("tab", tab);
  window.history.replaceState({}, "", url);
}

async function countRows(staticFilters) {
  try {
    const params = new URLSearchParams({ ...staticFilters, select: "id" });
    const res = await fetch(`${SUPABASE_URL()}/rest/v1/${QUEUE_RESOURCE}?${params}`, {
      headers: { ...authenticatedHeadersHash(), Prefer: "count=exact", Range: "0-0" },
    });
    const total = res.headers.get("Content-Range")?.split("/")[1];
    return total && total !== "*" ? parseInt(total, 10) : null;
  } catch {
    return null;
  }
}

function linkedinHref(value) {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function EmailWithDomain({ email }) {
  const at = email ? email.lastIndexOf("@") : -1;
  if (at < 0) return email || null;
  return (
    <>
      {email.slice(0, at + 1)}
      <strong className="text-body">{email.slice(at + 1)}</strong>
    </>
  );
}

EmailWithDomain.propTypes = { email: PropTypes.string };

function UserCell({ row }) {
  const v1Url = globalThis.Qwoted?.V1_URL;
  const position = row.title_at_workplace || row.headline;
  return (
    <div>
      <a href={`/admin/users/${row.account_user_id}/trust`} className="text-decoration-none fw-medium me-2">
        {row.user_name}
      </a>
      {row.user_type && (
        <span className="badge bg-light text-dark border">
          {USER_TYPE_LABELS[row.user_type] || humanize(row.user_type)}
        </span>
      )}
      <div className="text-muted small text-break">
        <EmailWithDomain email={row.email} />
      </div>
      {position && <div className="small text-break">{position}</div>}
      {row.first_request_id && (
        <div className="small text-break">
          First request:{" "}
          <a href={`/admin/requests/${row.first_request_id}`} className="text-decoration-none">
            {row.first_request_title || "Untitled"}
          </a>
          {!row.first_request_posted_at && <span className="text-muted"> (draft)</span>}
        </div>
      )}
      <div className="small d-flex flex-wrap gap-2">
        {row.linkedin_url && (
          <a href={linkedinHref(row.linkedin_url)} target="_blank" rel="noopener noreferrer">
            LinkedIn
          </a>
        )}
        {row.profile_slug && (
          <a href={`/profiles/${row.profile_slug}`} target="_blank" rel="noopener noreferrer">
            Profile
          </a>
        )}
        {v1Url && row.v1_user_id && (
          <a href={`${v1Url}/users/${row.v1_user_id}`} target="_blank" rel="noopener noreferrer">
            V1 user
          </a>
        )}
      </div>
      <div>
        {row.vetted && <span className="badge bg-success me-1">Vetted</span>}
        {row.under_audit_at && <span className="badge bg-danger me-1">Under audit</span>}
        {row.disabled_at && <span className="badge bg-dark me-1">Disabled</span>}
      </div>
    </div>
  );
}

UserCell.propTypes = { row: PropTypes.shape({}).isRequired };

function SignupCell({ row }) {
  const utms = UTM_KEYS.filter((key) => row[key]).map((key) => `${key.replace("utm_", "")}: ${row[key]}`);
  const referrer = row.referrer || row.referring_domain;
  if (!row.signup_code && !referrer && utms.length === 0) {
    return <span className="text-muted small">No signup context</span>;
  }
  return (
    <div className="small text-break">
      {row.signup_code && (
        <div>
          Code: <code>{row.signup_code}</code>
          {row.signup_code_name && ` (${row.signup_code_name})`}
        </div>
      )}
      {referrer && <div>Referrer: {referrer}</div>}
      {utms.length > 0 && <div className="text-muted">{utms.join(" · ")}</div>}
    </div>
  );
}

SignupCell.propTypes = { row: PropTypes.shape({}).isRequired };

function BulkVetResult({ result }) {
  if (result.error) return <div className="alert alert-danger mt-3 mb-0">{result.error}</div>;
  return (
    <div className={`alert mt-3 mb-0 ${result.conflicts.length ? "alert-warning" : "alert-success"}`}>
      Vetted {result.vetted} {result.vetted === 1 ? "user" : "users"}.
      {result.conflicts.length > 0 && (
        <>
          {" "}
          {result.conflicts.length} could not be vetted:
          <ul className="mb-0">
            {result.conflicts.map((conflict) => (
              <li key={conflict.id}>
                {conflict.name || `Decision ${conflict.id}`}: {conflict.error}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

BulkVetResult.propTypes = {
  result: PropTypes.shape({
    error: PropTypes.string,
    vetted: PropTypes.number,
    conflicts: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

function NewUsersAdminPanel({ ruleActionOptions, appliesToOptions, verificationEnabled, reviewWindowDays }) {
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [counts, setCounts] = useState({});
  const [userType, setUserType] = useState("all");
  const [pageRows, setPageRows] = useState([]);
  // Decision id => user name, for the bulk Vet.
  const [selected, setSelected] = useState(() => new Map());
  const [conflicts, setConflicts] = useState({});
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // A day wider than the server's window, so clock skew or a page left open never hides a row.
  const windowStart = useMemo(
    () => new Date(Date.now() - (reviewWindowDays + 1) * DAY_MS).toISOString(),
    [reviewWindowDays],
  );

  const refresh = useCallback(() => setRefreshTrigger((n) => n + 1), []);

  const resetBulk = useCallback(() => {
    setSelected(new Map());
    setConflicts({});
    setBulkResult(null);
  }, []);

  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);
      setTabInUrl(tab);
      resetBulk();
      refresh();
    },
    [refresh, resetBulk],
  );

  const handleUserTypeChange = (key) => {
    setUserType(key);
    resetBulk();
  };

  useEffect(() => {
    const onPopState = () => setActiveTab(getTabFromUrl());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all(REVIEW_TAB_KEYS.map((key) => countRows(tabFilters(TAB_CONFIGS[key], windowStart)))).then((values) => {
      if (active) setCounts(Object.fromEntries(REVIEW_TAB_KEYS.map((key, i) => [key, values[i]])));
    });
    return () => {
      active = false;
    };
  }, [refreshTrigger, windowStart]);

  const isRulesTab = activeTab === "rules";
  const isNewTab = activeTab === "new";
  const tabConfig = TAB_CONFIGS[activeTab] || TAB_CONFIGS.all;
  const columns = COLUMNS_BY_TAB[activeTab] || COLUMNS;

  const staticFilters = useMemo(() => {
    const filters = tabFilters(tabConfig, windowStart);
    const filter = isNewTab && USER_TYPE_FILTERS.find((option) => option.key === userType)?.filter;
    return filter ? { ...filters, user_type: filter } : filters;
  }, [isNewTab, userType, tabConfig, windowStart]);

  // Wrapped to keep the shown page's rows for "Select page".
  const dataAdapter = useMemo(() => {
    const adapter = createPostgRESTDataAdapter({
      endpoint: `${SUPABASE_URL()}/rest/v1`,
      resource: QUEUE_RESOURCE,
      headers: authenticatedHeadersHash(),
      staticFilters,
      columns,
      searchColumns: SEARCH_COLUMNS,
    });
    return {
      fetchData: async (args) => {
        const result = await adapter.fetchData(args);
        setPageRows(result.rows || []);
        return result;
      },
    };
  }, [staticFilters, columns]);

  const toggleSelected = (row) => {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row.user_name);
      return next;
    });
  };

  const pageSelected = pageRows.length > 0 && pageRows.every((row) => selected.has(row.id));

  const togglePage = () => {
    setSelected((current) => {
      const next = new Map(current);
      pageRows.forEach((row) => (pageSelected ? next.delete(row.id) : next.set(row.id, row.user_name)));
      return next;
    });
  };

  async function vetSelected() {
    const names = selected;
    setBulkBusy(true);
    try {
      const response = await fetch(`${DECISIONS_URL}/vet`, {
        method: "POST",
        headers: writeHeaders(),
        body: JSON.stringify({ ids: [...names.keys()] }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setBulkResult({ error: data.error || "Could not vet the selected users." });
        return;
      }
      const failed = (data.conflicts || []).map((conflict) => ({ ...conflict, name: names.get(conflict.id) }));
      setConflicts(Object.fromEntries(failed.map((conflict) => [conflict.id, conflict.error])));
      setBulkResult({ vetted: (data.vetted || []).length, conflicts: failed });
      setSelected(new Map());
      refresh();
    } catch {
      setBulkResult({ error: "Network error - please try again." });
    } finally {
      setBulkBusy(false);
    }
  }

  const renderVerdict = (row) => {
    if (!row.review_verdict) {
      if (row.resolved_elsewhere) {
        return (
          <span className="badge bg-light text-dark border">
            {RESOLVED_ELSEWHERE_LABELS[row.resolved_elsewhere] || humanize(row.resolved_elsewhere)}
          </span>
        );
      }
      return <span className="text-muted">—</span>;
    }
    return (
      <div>
        <span className={`badge ${VERDICT_BADGE[row.review_verdict] || "bg-secondary"}`}>
          {VERDICT_LABELS[row.review_verdict] || humanize(row.review_verdict)}
        </span>
        {row.reviewed_by_name && <div className="text-muted small">by {row.reviewed_by_name}</div>}
        {row.review_comment && <div className="small fst-italic text-break">“{row.review_comment}”</div>}
      </div>
    );
  };

  const renderCell = (row, column) => {
    if (column === "id") {
      return (
        <input
          type="checkbox"
          className="form-check-input"
          aria-label={`Select ${row.user_name}`}
          checked={selected.has(row.id)}
          onChange={() => toggleSelected(row)}
        />
      );
    }
    if (column === "user_name") return <UserCell row={row} />;
    if (column === "signup_code") return <SignupCell row={row} />;
    if (column === "user_created_at" || column === "created_at") {
      return row[column] ? formatTimeAgo(row[column]) : "—";
    }
    if (column === "ip") {
      return (
        <div>
          {row.ip ? <code>{row.ip}</code> : <span className="text-muted">No IP</span>}
          <div className="small">{row.country || <span className="text-muted">No country</span>}</div>
          {row.ip_source && <div className="text-muted small">{IP_SOURCE_LABELS[row.ip_source] || row.ip_source}</div>}
        </div>
      );
    }
    if (column === "matched_rules") {
      return (
        <div title={(row.reasons || []).join("\n")}>
          <MatchedRules rules={row.matched_rules} inputsMissing={row.inputs_missing} />
        </div>
      );
    }
    if (column === "outcome") {
      return (
        <div>
          <span className={`badge ${OUTCOME_BADGE[row.outcome] || "bg-secondary"}`}>
            {OUTCOME_LABELS[row.outcome] || humanize(row.outcome)}
          </span>
          {row.reason && <div className="text-muted small">{humanize(row.reason)}</div>}
        </div>
      );
    }
    if (column === "review_verdict") return renderVerdict(row);
    return undefined;
  };

  const renderActions = REVIEW_TAB_KEYS.includes(activeTab)
    ? (row) => (
        <div>
          {conflicts[row.id] && <div className="text-danger small mb-1">{conflicts[row.id]}</div>}
          <UserVerificationActions decision={row} onComplete={refresh} vertical />
        </div>
      )
    : undefined;

  const newTabToolbar = isNewTab && (
    <>
      <div className="btn-group btn-group-sm" role="group" aria-label="User type">
        {USER_TYPE_FILTERS.map((option) => (
          <button
            key={option.key}
            type="button"
            className={`btn btn-outline-secondary ${userType === option.key ? "active" : ""}`}
            aria-pressed={userType === option.key}
            onClick={() => handleUserTypeChange(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary text-nowrap"
        onClick={togglePage}
        disabled={pageRows.length === 0}
      >
        {pageSelected ? "Unselect page" : "Select page"}
      </button>
      <button
        type="button"
        className="btn btn-sm btn-success text-nowrap"
        onClick={vetSelected}
        disabled={selected.size === 0 || bulkBusy}
      >
        {bulkBusy ? "Vetting…" : `Vet selected (${selected.size})`}
      </button>
    </>
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">New Users</h1>
      </div>

      {!verificationEnabled && (
        <div className="alert alert-secondary">
          Automated user verification is switched off, so no new signups are being evaluated.
        </div>
      )}

      <ul className="nav nav-tabs mb-0">
        {TABS.map((tab) => (
          <li className={`nav-item ${tab.end ? "ms-auto" : ""}`} key={tab.key}>
            <button
              type="button"
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.icon && <i className={`fa-solid ${tab.icon} me-2`} aria-hidden="true" />}
              {tab.label}
              {tab.badge && counts[tab.key] > 0 && (
                <span className={`badge rounded-pill ${tab.badge} ms-2`}>{counts[tab.key]}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {isRulesTab ? (
        <div className="mt-3">
          <UserVerificationRulesPanel actionOptions={ruleActionOptions} appliesToOptions={appliesToOptions} />
        </div>
      ) : (
        <>
          {isNewTab && (
            <p className="text-muted small mt-3 mb-0">
              Signups from the last {reviewWindowDays} days that nobody has vetted or disabled. A signup nobody reviews
              leaves this tab after that and stays on the All tab.
            </p>
          )}
          {activeTab === "suspicious" && (
            <p className="text-muted small mt-3 mb-0">
              Suspicious signups from the last {reviewWindowDays} days. A suspicion nobody acts on clears itself after
              that and stays on the All tab.
            </p>
          )}
          {isNewTab && bulkResult && <BulkVetResult result={bulkResult} />}
          {/* Keyed on the user type too, so a new filter starts on page one. */}
          <SearchableTable
            key={isNewTab ? `${activeTab}-${userType}` : activeTab}
            columns={columns}
            searchColumns={SEARCH_COLUMNS}
            dataAdapter={dataAdapter}
            pageSize={25}
            defaultSort={tabConfig.defaultSort}
            renderCell={renderCell}
            renderActions={renderActions}
            enableFiltering={false}
            refreshTrigger={refreshTrigger}
            toolbar={newTabToolbar || null}
          />
        </>
      )}
    </div>
  );
}

NewUsersAdminPanel.propTypes = {
  ruleActionOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  appliesToOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  verificationEnabled: PropTypes.bool,
  reviewWindowDays: PropTypes.number.isRequired,
};

export default NewUsersAdminPanel;
